import { useState, useEffect, useCallback, useMemo } from "react";
import useAuthStore from "../../store/authStore";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminLayout from "../../layouts/AdminLayout";
import ManagerLayout from "../../layouts/ManagerLayout";
import EmployeeLayout from "../../layouts/EmployeeLayout";
import apiClient from "../../api/apiClient";
import { getAttendance, getRemoteAttendance, raiseEditTicket, getEditTickets, editAttendance, resolveRemoteAttendance, getWfhPeriods, resolveWfhPeriod, cancelEditTicket } from "../../api/attendanceService";

function parseISOAsLocal(isoStr) {
  if (!isoStr) return null;
  const local = isoStr.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
  return new Date(local);
}

function sanitizeText(str) {
  if (!str) return "";
  return str
    .replace(/<[^>]*>/g, "") // strip HTML tags
    .replace(/[;'"\\]/g, "") // strip database special characters
    .trim();
}

function fmtTimeFromISO(isoStr) {
  if (!isoStr) return "—";
  const d = parseISOAsLocal(isoStr);
  if (!d || isNaN(d)) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function isoToDateTimeLocal(isoStr) {
  if (!isoStr) return "";
  const d = parseISOAsLocal(isoStr);
  if (!d || isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function dateTimeLocalToUTC(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr);
  if (isNaN(d)) return null;
  return d.toISOString();
}

const ATTENDANCE_STATUSES = ["PRESENT", "ABSENT", "HALF_DAY", "WORK_FROM_HOME", "HOLIDAY", "WEEK_OFF", "ON_LEAVE"];

function statusColor(s) {
  switch (s) {
    case "PRESENT": return "bg-emerald-500/20 text-emerald-300";
    case "ABSENT": return "bg-red-500/20 text-red-300";
    case "HOLIDAY": return "bg-purple-500/20 text-purple-300";
    case "WEEK_OFF": return "bg-indigo-500/20 text-indigo-300";
    case "ON_LEAVE": return "bg-orange-500/20 text-orange-300";
    case "HALF_DAY": return "bg-yellow-500/20 text-yellow-300";
    case "WORK_FROM_HOME": return "bg-cyan-500/20 text-cyan-300";
    case "REMOTE_PENDING": return "bg-yellow-500/20 text-yellow-200";
    default: return "bg-slate-500/20 text-slate-300";
  }
}

function formatHours(hours) {
  if (hours == null || hours === 0) return "—";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const ClearableTimeInput = ({ value, onChange, onClear }) => {
  // Parse YYYY-MM-DDTHH:MM or HH:MM
  let hourVal = "";
  let minVal = "";
  let periodVal = "AM";

  if (value) {
    const timePart = value.includes("T") ? value.split("T")[1] : value;
    if (timePart) {
      const [hStr, mStr] = timePart.split(":");
      const h24 = parseInt(hStr || "0", 10);
      minVal = mStr ? mStr.slice(0, 2) : "00";
      periodVal = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
      hourVal = String(h12).padStart(2, "0");
    }
  }

  const handleHourChange = (newHour) => {
    if (!newHour) {
      onClear();
      return;
    }
    const h12 = parseInt(newHour, 10);
    const m = minVal || "00";
    const p = periodVal || "AM";
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const handleMinChange = (newMin) => {
    const h = hourVal || "09";
    const h12 = parseInt(h, 10);
    const m = newMin || "00";
    const p = periodVal || "AM";
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const handlePeriodChange = (newPeriod) => {
    const h = hourVal || "09";
    const h12 = parseInt(h, 10);
    const m = minVal || "00";
    const p = newPeriod;
    let h24 = h12;
    if (p === "PM" && h12 < 12) h24 += 12;
    if (p === "AM" && h12 === 12) h24 = 0;

    onChange(`${String(h24).padStart(2, "0")}:${m}`);
  };

  const hoursList = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  const minutesList = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  return (
    <div className="flex items-center gap-1 bg-white/5 border border-cyan-500/20 rounded-xl p-1 sm:p-1.5 min-w-[145px] sm:min-w-[175px] shadow-lg backdrop-blur-md justify-between mx-auto w-fit">
      {/* Hour Select */}
      <select
        value={hourVal}
        onChange={(e) => handleHourChange(e.target.value)}
        className="bg-slate-900 border border-white/10 rounded px-1 py-0.5 text-white text-[10px] sm:text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer"
      >
        <option value="" className="text-white/40">HH</option>
        {hoursList.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>

      <span className="text-cyan-400 font-bold text-xs">:</span>

      {/* Minute Select */}
      <select
        value={minVal}
        onChange={(e) => handleMinChange(e.target.value)}
        disabled={!hourVal}
        className="bg-slate-900 border border-white/10 rounded px-1 py-0.5 text-white text-[10px] sm:text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="" className="text-white/40">MM</option>
        {minutesList.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>

      {/* AM/PM Select */}
      <select
        value={periodVal}
        onChange={(e) => handlePeriodChange(e.target.value)}
        disabled={!hourVal}
        className="bg-slate-900 border border-white/10 rounded px-1 py-0.5 text-white text-[10px] sm:text-[11px] font-bold outline-none focus:border-cyan-400 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>

      {/* Clear Button */}
      {value ? (
        <button
          type="button"
          onClick={onClear}
          title="Clear time"
          className="ml-1 w-4 sm:w-5 h-4 sm:h-5 rounded bg-rose-500/20 hover:bg-rose-500/40 text-rose-300 hover:text-rose-100 text-[10px] font-bold transition flex items-center justify-center border border-rose-500/30 flex-shrink-0"
        >
          ✕
        </button>
      ) : (
        <span className="ml-1 w-4 sm:w-5 h-4 sm:h-5 rounded bg-white/5 text-white/20 text-[10px] flex items-center justify-center flex-shrink-0">–</span>
      )}
    </div>
  );
};

function ReasonCell({ remoteReason, ticketReason }) {
  const hasRemote = !!remoteReason;
  const hasTicket = !!ticketReason;
  if (!hasRemote && !hasTicket) return <span className="text-white/20 text-xs italic">—</span>;
  return (
    <div className="flex flex-col gap-1.5 max-w-[160px] sm:max-w-[200px]">
      {hasRemote && (
        <div className="flex items-start gap-1.5">
          <span className="flex-shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/25 leading-none">REMOTE</span>
          <span className="text-white/65 text-[11px] leading-snug break-words">{remoteReason}</span>
        </div>
      )}
      {hasTicket && (
        <div className="flex items-start gap-1.5">
          <span className="flex-shrink-0 mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-500/15 text-purple-300 border border-purple-500/25 leading-none">TICKET</span>
          <span className="text-white/65 text-[11px] leading-snug break-words">{ticketReason}</span>
        </div>
      )}
    </div>
  );
}

function RemoteModal({ employeeId, onClose, onApproved }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [actionErr, setActionErr] = useState("");
  const [tab, setTab] = useState("PENDING"); // PENDING, APPROVED, ALL

  const load = useCallback(async () => {
    setLoading(true);
    setActionErr("");
    try {
      const params = { employee_id: employeeId };
      if (tab !== "ALL") {
        params.status = tab;
      } else {
        params.include_all = "true";
      }
      const res = await getRemoteAttendance(params);
      setRequests(res.data?.remote_requests || []);
    } catch (err) {
      console.error("Failed to load remote requests:", err);
      setActionErr("Could not load remote requests. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [employeeId, tab]);

  useEffect(() => { load(); }, [load]);

  const filtered = requests;

  const handleAction = async (id, action) => {
    setProcessing(id);
    setActionErr("");
    try {
      await resolveRemoteAttendance(id, action);
      await load();
      onApproved();
    } catch (err) {
      const d = err?.response?.data;
      setActionErr(d?.error || d?.detail || `Failed to ${action} request.`);
    } finally {
      setProcessing(null);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm px-3 sm:px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !processing) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1a2540] border border-white/20 rounded-2xl p-4 sm:p-6 w-full max-w-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "85vh" }}>

        <div className="flex items-center justify-between mb-4 sm:mb-5 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-base sm:text-lg">🛰️</div>
            <div>
              <h3 className="text-white font-bold text-base sm:text-lg">Remote Attendance Requests</h3>
              <p className="text-white/50 text-xs">
                Employee: {employeeId} · {tab === "PENDING" ? "Pending approvals" : tab === "APPROVED" ? "Approved requests" : "All requests history"}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={!!processing} className="text-white/40 hover:text-white text-xl transition disabled:opacity-40">✕</button>
        </div>

        <AnimatePresence>
          {actionErr && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium flex-shrink-0">
              ⚠️ {actionErr}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex border-b border-white/10 mb-4 flex-shrink-0">
          {["PENDING", "APPROVED", "ALL"].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs font-bold transition-all relative ${tab === t ? "text-cyan-400" : "text-white/40 hover:text-white/60"}`}>
              {t}
              {tab === t && <motion.div layoutId="activeTabRemote" className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" />}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 pr-1 custom-scrollbar">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-3 opacity-50">{tab === "PENDING" ? "✅" : "📭"}</div>
              <p className="text-white/40 text-sm font-medium">No {tab.toLowerCase()} requests found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((req) => (
                <div key={req.id} className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
                        <span className="text-white font-semibold text-sm">📅 {req.attendance_date}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${req.remote_approval_status === "APPROVED" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" :
                          req.remote_approval_status === "REJECTED" ? "bg-red-500/10 text-red-400 border-red-500/20" :
                            "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                          }`}>
                          {req.remote_approval_status || "PENDING"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-1 text-xs text-white/60 mb-3">
                        <span>🕐 In: <span className="text-white/80 font-medium">{fmtTimeFromISO(req.check_in)}</span></span>
                        <span>🕔 Out: <span className="text-white/80 font-medium">{req.check_out ? fmtTimeFromISO(req.check_out) : "—"}</span></span>
                        {req.working_hours != null && (
                          <span>⌚ Duration: <span className="text-white/80 font-medium">{formatHours(req.working_hours)}</span></span>
                        )}
                        <span>👤 Status: <span className="text-white/80 font-medium">{req.status || "—"}</span></span>
                      </div>
                      {req.remote_reason && (
                        <div className="bg-white/5 rounded-lg px-3 py-2 text-xs text-white/70 border border-white/10">
                          💬 <em>"{req.remote_reason}"</em>
                        </div>
                      )}
                    </div>
                    {req.remote_approval_status === "PENDING" && (
                      <div className="flex sm:flex-col gap-2 flex-shrink-0">
                        <button onClick={() => handleAction(req.id, "approve")} disabled={processing === req.id}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold transition whitespace-nowrap">
                          {processing === req.id ? <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Wait…</span> : "✅ Approve"}
                        </button>
                        <button onClick={() => handleAction(req.id, "reject")} disabled={processing === req.id}
                          className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-bold transition whitespace-nowrap">
                          {processing === req.id ? <span className="flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Wait…</span> : "✕ Reject"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-white/30 text-[10px] uppercase tracking-widest font-bold">
            {filtered.length} {tab.toLowerCase()} request{filtered.length !== 1 ? "s" : ""}
            {(tab === "PENDING" || tab === "APPROVED") && filtered.length > 0 && " (max 3 per user)"}
          </p>
          <button onClick={onClose} disabled={!!processing}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition disabled:opacity-40">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function WfhPeriodManagementModal({ employeeId, onClose, onAction }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("PENDING"); // PENDING, APPROVED, ALL

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { employee_id: employeeId };
      if (tab !== "ALL") params.status = tab;
      const res = await getWfhPeriods(params);
      setPeriods(res.data.wfh_periods || []);
    } catch (err) {
      console.error("Failed to load WFH periods:", err);
      setError("Could not load WFH periods.");
    } finally {
      setLoading(false);
    }
  }, [employeeId, tab]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    setProcessing(id);
    setError("");
    try {
      await resolveWfhPeriod(id, action);
      await load();
      onAction();
    } catch (err) {
      const d = err?.response?.data;
      setError(d?.error || d?.detail || `Failed to ${action} period.`);
    } finally {
      setProcessing(null);
    }
  };

  const filtered = periods;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm px-3 sm:px-4"
      onClick={(e) => { if (e.target === e.currentTarget && !processing) onClose(); }}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1a2540] border border-white/20 rounded-2xl p-4 sm:p-6 w-full max-w-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: "85vh" }}>

        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-lg">📅</div>
            <div>
              <h3 className="text-white font-bold text-base sm:text-lg">WFH Period Management</h3>
              <p className="text-white/50 text-xs">Employee: {employeeId}</p>
            </div>
          </div>
          <button onClick={onClose} disabled={!!processing} className="text-white/40 hover:text-white text-xl transition disabled:opacity-40">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-white/10 flex-shrink-0">
          {["PENDING", "APPROVED", "ALL"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`pb-2 px-1 text-xs font-bold transition-all relative ${tab === t ? "text-violet-400" : "text-white/40 hover:text-white/60"}`}>
              {t}
              {tab === t && <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-violet-400" />}
            </button>
          ))}
        </div>

        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium flex-shrink-0">
              ⚠️ {error}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-white/40 text-sm font-medium italic">No {tab.toLowerCase()} periods found.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((p) => (
                <div key={p.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-white font-semibold text-sm">📅 {p.from_date} to {p.to_date}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${p.status === "PENDING" ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" :
                          p.status === "APPROVED" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" :
                            "bg-red-500/20 text-red-300 border-red-500/30"
                          }`}>
                          {p.status}
                        </span>
                      </div>
                      <div className="text-xs text-white/65 leading-relaxed bg-white/5 p-2.5 rounded-lg border border-white/5">
                        💬 "{p.reason}"
                      </div>
                      <div className="mt-2 text-[10px] text-white/40 flex items-center gap-3">
                        <span>Submitted: {new Date(p.created_at).toLocaleDateString()}</span>
                        {p.approved_by_name && <span>Action by: {p.approved_by_name}</span>}
                      </div>
                    </div>
                    <div className="flex sm:flex-col gap-2">
                      {p.status === "PENDING" ? (
                        <>
                          <button onClick={() => handleAction(p.id, "approve")} disabled={processing === p.id}
                            className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition">
                            Approve
                          </button>
                          <button onClick={() => handleAction(p.id, "reject")} disabled={processing === p.id}
                            className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-600 text-white text-xs font-bold transition">
                            Reject
                          </button>
                        </>
                      ) : p.status === "APPROVED" ? (
                        <button onClick={() => handleAction(p.id, "revoke")} disabled={processing === p.id}
                          className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-orange-600/80 hover:bg-orange-600 text-white text-xs font-bold transition">
                          Revoke
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-white/30 text-[10px] uppercase tracking-widest font-bold">
            {filtered.length} {tab.toLowerCase()} period{filtered.length !== 1 ? "s" : ""}
            {(tab === "PENDING" || tab === "APPROVED") && filtered.length > 0 && " (max 3 per user)"}
          </p>
          <button onClick={onClose} disabled={!!processing}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function AttendanceReport({ isPopup = false, onClose = null }) {
  const role = useAuthStore((state) => state.role);
  const loggedInEmployeeId = useAuthStore((state) => state.employeeId);
  const Layout = role === "ADMIN" ? AdminLayout : role === "MANAGER" ? ManagerLayout : EmployeeLayout;
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const targetEmployee = employeeId || loggedInEmployeeId;
  const isOwnReport = targetEmployee === loggedInEmployeeId;
  const currentMonth = String(new Date().getMonth() + 1);
  const currentYear = String(new Date().getFullYear());

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditColumn, setShowEditColumn] = useState(isPopup);
  const showReasonColumn = isPopup;
  const [ticketMap, setTicketMap] = useState({});
  const [ticketModal, setTicketModal] = useState(null);
  const [ticketReason, setTicketReason] = useState("");
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [pendingRemoteCount, setPendingRemoteCount] = useState(0);
  const [showWfhPeriodModal, setShowWfhPeriodModal] = useState(false);
  const [pendingWfhCount, setPendingWfhCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [pendingTicketCount, setPendingTicketCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // ── Resolve superAdmin flag once on mount ──────────────────────────────────
  useEffect(() => {
    if (loggedInEmployeeId) {
      apiClient.get(`/accounts/api/users/${loggedInEmployeeId}/`)
        .then((res) => setIsSuperAdmin(res.data.user?.is_superuser === true))
        .catch(() => setIsSuperAdmin(false));
    }
  }, [loggedInEmployeeId]);

  // ── Single parallel load — attendance + tickets + remote + wfh at once ─────
  const loadPendingTicketCount = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await getEditTickets({ status: "PENDING", employee_id: targetEmployee });
      const tickets = res.data.tickets || [];
      setPendingTicketCount(tickets.filter((t) => t.request_type === "ATTENDANCE").length);
    } catch { setPendingTicketCount(0); }
  }, [isSuperAdmin, targetEmployee]);

  useEffect(() => { loadPendingTicketCount(); }, [loadPendingTicketCount]);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      // Fire attendance + tickets in parallel; admin also gets remote + wfh counts
      const baseParams = { employee_id: employeeId ? targetEmployee : undefined, month, year };
      const ticketParams = { employee_id: targetEmployee };

      const parallelFetches = [
        getAttendance(baseParams),
        getEditTickets(ticketParams),
      ];
      if (isSuperAdmin) {
        parallelFetches.push(
          apiClient.get("/attendance/api/remote-attendance/", { params: { employee_id: targetEmployee } }),
          getWfhPeriods({ status: "PENDING", employee_id: targetEmployee }),
        );
      }

      const [attRes, ticketRes, remoteRes, wfhRes] = await Promise.all(parallelFetches);

      // Process attendance
      const data = attRes.data.attendances || [];
      setRecords(data.map((a) => ({
        id: a.id,
        date: new Date(a.date + "T00:00:00").toLocaleDateString("en-GB"),
        rawDate: a.date,
        employee: targetEmployee,
        status: a.status,
        checkIn: a.check_in ? fmtTimeFromISO(a.check_in) : "-",
        checkOut: a.check_out ? fmtTimeFromISO(a.check_out) : "-",
        workingHours: a.working_hours ?? null,
        rawCheckIn: a.check_in || null,
        rawCheckOut: a.check_out || null,
        method: a.method,
        remoteApprovalStatus: a.remote_approval_status,
        remoteReason: a.remote_reason || null,
        hasOverride: a.has_override || false,
      })));

      // Process tickets
      const tickets = (ticketRes.data.tickets || []).filter((t) => t.request_type === "ATTENDANCE");
      const map = {};
      tickets.forEach((t) => { if (!map[t.reference_id]) map[t.reference_id] = { id: t.id, status: t.status, reason: t.reason }; });
      setTicketMap(map);

      // Process admin-only counts
      if (isSuperAdmin) {
        setPendingRemoteCount((remoteRes?.data?.remote_requests || []).length);
        setPendingWfhCount((wfhRes?.data?.wfh_periods || []).length);
        loadPendingTicketCount();
      }
    } catch (err) {
      console.error("Attendance fetch error:", err);
    } finally {
      setLoading(false);
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, targetEmployee, employeeId, isSuperAdmin]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const loadTickets = useCallback(async () => {
    try {
      const res = await getEditTickets({ employee_id: targetEmployee });
      const tickets = (res.data.tickets || []).filter((t) => t.request_type === "ATTENDANCE");
      const map = {};
      tickets.forEach((t) => { if (!map[t.reference_id]) map[t.reference_id] = { id: t.id, status: t.status, reason: t.reason }; });
      setTicketMap(map);
    } catch (err) {
      console.error("Ticket fetch error:", err);
    }
  }, [targetEmployee]);

  const loadPendingRemoteCount = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await apiClient.get("/attendance/api/remote-attendance/", { params: { employee_id: targetEmployee } });
      setPendingRemoteCount((res.data?.remote_requests || []).length);
    } catch { setPendingRemoteCount(0); }
  }, [isSuperAdmin, targetEmployee]);

  const loadPendingWfhCount = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await getWfhPeriods({ status: "PENDING", employee_id: targetEmployee });
      setPendingWfhCount((res.data?.wfh_periods || []).length);
    } catch { setPendingWfhCount(0); }
  }, [isSuperAdmin, targetEmployee]);

  const handleRaiseTicket = async () => {
    const sanitizedReason = sanitizeText(ticketReason);
    if (!sanitizedReason) { setTicketError("Please provide a valid, safe reason."); return; }
    setTicketSubmitting(true);
    setTicketError("");
    try {
      await raiseEditTicket({ request_type: "ATTENDANCE", reference_id: ticketModal.attendanceId, reason: sanitizedReason });
      setTicketModal(null);
      setTicketReason("");
      loadTickets();
    } catch (err) {
      setTicketError(err.response?.data?.error || "Failed to raise ticket.");
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleCancelTicket = async (ticketId) => {
    setCancellingId(ticketId);
    try {
      await cancelEditTicket(ticketId);
      loadAttendance();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel ticket.");
    } finally {
      setCancellingId(null);
    }
  };

  const startEditing = (record) => {
    setEditForm({
      status: record.status,
      check_in: record.rawCheckIn ? isoToDateTimeLocal(record.rawCheckIn) : null,
      check_out: record.rawCheckOut ? isoToDateTimeLocal(record.rawCheckOut) : null,
      rawDate: record.rawDate,
    });
    setEditError("");
    setEditingId(record.id);
  };

  const cancelEditing = () => { setEditingId(null); setEditError(""); };

  const handleSaveEdit = async (recordId) => {
    setEditSaving(true);
    setEditError("");
    try {
      const payload = { status: editForm.status };

      if (editForm.check_in === null) {
        payload.check_in = null;
      } else if (editForm.check_in) {
        const timePart = editForm.check_in.includes("T") ? editForm.check_in.split("T")[1] : editForm.check_in;
        const localDateTime = `${editForm.rawDate}T${timePart}`;
        payload.check_in = dateTimeLocalToUTC(localDateTime);
      }

      if (editForm.check_out === null) {
        payload.check_out = null;
      } else if (editForm.check_out) {
        const timePart = editForm.check_out.includes("T") ? editForm.check_out.split("T")[1] : editForm.check_out;
        const localDateTime = `${editForm.rawDate}T${timePart}`;
        payload.check_out = dateTimeLocalToUTC(localDateTime);
      }

      await editAttendance(recordId, payload);
      setEditingId(null);
      loadAttendance();
      loadTickets();
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to save changes.");
    } finally {
      setEditSaving(false);
    }
  };

  const downloadCSV = () => {
    if (!records.length) return;
    const headers = ["Date", "Employee", "Status", "Check In", "Check Out", "Duration", "Method", "Remote Reason", "Ticket Reason"];
    const rows = records.map((r) => {
      const ticket = ticketMap[r.id];
      return [r.date, r.employee, r.status, r.checkIn === "-" ? "" : r.checkIn, r.checkOut === "-" ? "" : r.checkOut, formatHours(r.workingHours), r.method || "", r.remoteReason || "", ticket?.reason || ""];
    });
    const csv = "data:text/csv;charset=utf-8," + [headers, ...rows].map((e) => e.map((v) => `"${v}"`).join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csv));
    link.setAttribute("download", `AttendanceReport_${targetEmployee}_${month}_${year}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
      return;
    }
    if (location.state?.from) navigate(location.state.from);
    else if (role === "ADMIN") navigate("/admin/dashboard");
    else if (role === "MANAGER") navigate("/manager/dashboard");
    else navigate("/employee/dashboard");
  };

  const TicketCell = ({ record }) => {
    const ticket = ticketMap[record.id];
    if (editingId !== null && editingId === record.id) return null;
    if (role === "ADMIN") {
      if (ticket?.status === "PENDING") return (
        <button onClick={() => startEditing(record)} className="px-2 sm:px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-slate-900 text-xs font-bold transition flex items-center gap-1 mx-auto">
          ✏️ Edit <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-900/30 text-[10px] font-semibold">Pending</span>
        </button>
      );
      if (ticket?.status === "APPROVED") return (
        <button onClick={() => startEditing(record)} className="px-2 sm:px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition flex items-center gap-1 mx-auto">
          ✏️ Edit <span className="ml-1 px-1.5 py-0.5 rounded bg-white/20 text-[10px] font-semibold">Approved</span>
        </button>
      );
      return <span className="text-white/20 text-xs">—</span>;
    }
    if (!isOwnReport) {
      if (ticket?.status === "PENDING") return <span className="px-2 py-1 rounded-lg bg-yellow-500/20 text-yellow-300 border border-yellow-400/30 text-xs font-semibold">🎫 Pending</span>;
      if (ticket?.status === "APPROVED") return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-semibold">✅ Approved</span>;
      return <span className="text-white/20 text-xs">—</span>;
    }
    if (ticket?.status === "PENDING") return (
      <button
        onClick={() => handleCancelTicket(ticket.id)}
        disabled={cancellingId === ticket.id}
        className="px-2 py-1 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-400/30 text-xs font-semibold transition active:scale-95 flex items-center gap-1 mx-auto"
        title="Click to cancel this pending ticket"
      >
        🎫 Cancel
      </button>
    );
    if (ticket?.status === "APPROVED") return <span className="px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-xs font-semibold">✅ Approved</span>;
    return (
      <button onClick={() => { setTicketError(""); setTicketReason(""); setTicketModal({ attendanceId: record.id }); }}
        className="px-2 py-1 rounded-lg bg-purple-600/80 hover:bg-purple-600 text-white text-xs font-bold transition mx-auto flex items-center gap-1">
        🎫 Raise Ticket
      </button>
    );
  };

  const monthOptions = [
    { value: "1", label: "January" }, { value: "2", label: "February" }, { value: "3", label: "March" },
    { value: "4", label: "April" }, { value: "5", label: "May" }, { value: "6", label: "June" },
    { value: "7", label: "July" }, { value: "8", label: "August" }, { value: "9", label: "September" },
    { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
  ];
  const yearOptions = ["2024", "2025", "2026"];
  const totalCols = 6 + (showReasonColumn ? 1 : 0) + (showEditColumn ? 1 : 0);

  const totalPages = Math.ceil(records.length / pageSize) || 1;
  const currentRecords = useMemo(() => {
    return records.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [records, currentPage]);

  const reportContent = (
    <>
      <div className={`flex flex-col items-center p-3 sm:p-6 w-full ${isPopup ? '' : 'min-h-screen'}`}>
        <div className="w-full max-w-7xl">

          {/* HEADER — hidden in popup/raise-ticket mode */}
          {!isPopup && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">📊</div>
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-white">Attendance Report</h2>
                  <p className="text-white/60 text-xs sm:text-sm">View attendance history</p>
                </div>
              </div>
              <button onClick={downloadCSV} disabled={records.length === 0}
                className="self-start sm:self-auto px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm font-semibold shadow transition">
                ⬇ Download
              </button>
            </motion.div>
          )}

          {/* FILTERS + ACTIONS — wrapping flex on mobile */}
          <div className="flex flex-wrap gap-2 sm:gap-4 items-end mb-5 sm:mb-6">
            <div className="relative">
              <label className="text-white/70 text-xs sm:text-sm">Month</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)}
                className="mt-1 appearance-none bg-transparent border border-white/30 backdrop-blur-md rounded-lg px-3 sm:px-4 py-2 pr-7 sm:pr-10 text-white text-xs sm:text-sm focus:outline-none focus:border-cyan-400 transition">
                <option value="0" className="bg-slate-900">All</option>
                {monthOptions.map((m) => <option key={m.value} value={m.value} className="bg-slate-900">{m.label}</option>)}
              </select>
              <div className="absolute right-2 sm:right-3 top-[30px] sm:top-[38px] pointer-events-none text-white/70 text-xs">▼</div>
            </div>

            <div className="relative">
              <label className="text-white/70 text-xs sm:text-sm">Year</label>
              <select value={year} onChange={(e) => setYear(e.target.value)}
                className="mt-1 appearance-none bg-transparent border border-white/30 backdrop-blur-md rounded-lg px-3 sm:px-4 py-2 pr-7 sm:pr-10 text-white text-xs sm:text-sm focus:outline-none focus:border-cyan-400 transition">
                <option value="0" className="bg-slate-900">All</option>
                {yearOptions.map((y) => <option key={y} value={y} className="bg-slate-900">{y}</option>)}
              </select>
              <div className="absolute right-2 sm:right-3 top-[30px] sm:top-[38px] pointer-events-none text-white/70 text-xs">▼</div>
            </div>




            {isSuperAdmin && (
              <div className="relative">
                <button onClick={() => setShowRemoteModal(true)}
                  className="relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition border bg-cyan-600/20 border-cyan-500/40 text-cyan-300 hover:bg-cyan-600/40">
                  🛰️ Remote
                  {pendingRemoteCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-4 sm:h-5 px-1 bg-yellow-500 rounded-full text-slate-900 text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{pendingRemoteCount}</span>
                  )}
                </button>
              </div>
            )}

            {isSuperAdmin && (
              <div className="relative">
                <button onClick={() => setShowWfhPeriodModal(true)}
                  className="relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs sm:text-sm transition border bg-violet-600/20 border-violet-500/40 text-violet-300 hover:bg-violet-600/40">
                  📅 WFH Period
                  {pendingWfhCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-4 sm:h-5 px-1 bg-violet-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-slate-900 animate-pulse">{pendingWfhCount}</span>
                  )}
                </button>
              </div>
            )}

            {isSuperAdmin && (
              <div className="relative">
                <button
                  onClick={() => setShowEditColumn((p) => !p)}
                  className={`relative flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg font-semibold
                              text-xs sm:text-sm transition border ${showEditColumn
                      ? "bg-purple-600 border-purple-500 text-white"
                      : "bg-white/10 border-white/20 text-white/80 hover:bg-white/20"
                    }`}
                >
                  ✏️ {showEditColumn ? "Hide Edit" : "Edit Req."}
                  {pendingTicketCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-4 sm:h-5 px-1 bg-red-500 rounded-full
                                     text-white text-[10px] font-bold flex items-center justify-center
                                     border-2 border-slate-900 animate-pulse">
                      {pendingTicketCount}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* TABLE — scrollable horizontally on mobile */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <div className="overflow-x-auto no-scrollbar relative">
              {/* Swipe visual indicator for narrow screens */}
              <div className="md:hidden absolute right-2 top-2 z-10 animate-pulse text-[10px] bg-slate-950/80 text-white/50 px-2 py-0.5 rounded-full pointer-events-none select-none border border-white/5">
                ← Swipe table to view more →
              </div>
              <table className="w-full text-xs sm:text-sm text-center min-w-[500px]">
                <thead className="bg-white/10 text-white">
                  <tr>
                    <th className="py-3 sm:py-4 px-2 sm:px-4 whitespace-nowrap">Date</th>
                    <th className="px-2 sm:px-4 whitespace-nowrap">Employee</th>
                    <th className="px-2 sm:px-4 whitespace-nowrap">Status</th>
                    <th className="px-2 sm:px-4 whitespace-nowrap">In</th>
                    <th className="px-2 sm:px-4 whitespace-nowrap">Out</th>
                    <th className="px-2 sm:px-4 whitespace-nowrap">Dur.</th>
                    {showReasonColumn && <th className="px-2 sm:px-4 whitespace-nowrap text-left">💬 Reason</th>}
                    {showEditColumn && <th className="px-2 sm:px-4 whitespace-nowrap">Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={totalCols} className="py-8 text-white/60">
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />Loading…
                      </span>
                    </td></tr>
                  )}
                  {!loading && records.length === 0 && (
                    <tr><td colSpan={totalCols} className="py-8 text-white/50">No attendance records found.</td></tr>
                  )}
                  {currentRecords.map((a, i) => {
                    const isEditing = editingId !== null && editingId === a.id;
                    const ticket = ticketMap[a.id];
                    return (
                      <tr key={i} className={`border-b border-white/5 transition ${isEditing ? "bg-cyan-500/10 ring-1 ring-inset ring-cyan-400/40" : "hover:bg-white/5"}`}>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-white whitespace-nowrap">{a.date}</td>
                        <td className="px-2 sm:px-4 text-white/80 text-[10px] sm:text-sm">{a.employee}</td>
                        <td className="px-2 sm:px-4">
                          {isEditing ? (
                            <select value={editForm.status || ""} onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                              className="bg-slate-800 border border-cyan-400/50 rounded-lg px-1 sm:px-2 py-1 text-white text-xs focus:outline-none w-full">
                              {ATTENDANCE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          ) : (
                            <div className="flex flex-col items-center justify-center gap-1">
                              <span className={`px-2 py-0.5 sm:py-1 rounded-lg text-[10px] sm:text-xs font-bold ${statusColor(a.status)}`}>{a.status}</span>
                              {(a.status === "PRESENT" || a.status === "HALF_DAY") && a.hasOverride && (
                                <span className="text-[8px] sm:text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded leading-none border border-emerald-500/20 shadow-sm whitespace-nowrap">
                                  Comp Off Earned
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 text-white/80">
                          {isEditing ? (
                            <ClearableTimeInput value={editForm.check_in} onChange={(val) => setEditForm((f) => ({ ...f, check_in: val }))} onClear={() => setEditForm((f) => ({ ...f, check_in: null }))} />
                          ) : (
                            <span className={a.checkIn === "-" ? "text-white/30 italic text-[10px]" : "text-xs sm:text-sm"}>{a.checkIn === "-" ? "—" : a.checkIn}</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 text-white/80">
                          {isEditing ? (
                            <ClearableTimeInput value={editForm.check_out} onChange={(val) => setEditForm((f) => ({ ...f, check_out: val }))} onClear={() => setEditForm((f) => ({ ...f, check_out: null }))} />
                          ) : (
                            <span className={a.checkOut === "-" ? "text-white/30 italic text-[10px]" : "text-xs sm:text-sm"}>{a.checkOut === "-" ? "—" : a.checkOut}</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-4">
                          <span className={a.workingHours ? "text-white/80 text-xs" : "text-white/25 text-xs italic"}>{formatHours(a.workingHours)}</span>
                        </td>
                        {showReasonColumn && (
                          <td className="px-2 sm:px-4 py-2 sm:py-3 text-left">
                            <ReasonCell remoteReason={a.remoteReason} ticketReason={ticket?.reason || null} />
                          </td>
                        )}
                        {showEditColumn && (
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            {isEditing ? (
                              <div className="flex flex-col items-center gap-1">
                                {editError && <p className="text-red-400 text-[10px] mb-1">{editError}</p>}
                                <div className="flex gap-1 sm:gap-2">
                                  <button onClick={() => handleSaveEdit(a.id)} disabled={editSaving}
                                    className="px-2 sm:px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold transition">
                                    {editSaving ? "…" : "✅"}
                                  </button>
                                  <button onClick={cancelEditing}
                                    className="px-2 sm:px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition">✕</button>
                                </div>
                              </div>
                            ) : <TicketCell record={a} />}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {!loading && records.length > pageSize && (
              <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02] border-t border-white/5 flex-wrap gap-2">
                <div className="text-white/60 text-xs select-none">
                  Showing <span className="font-semibold text-white">{Math.min((currentPage - 1) * pageSize + 1, records.length)}</span> to <span className="font-semibold text-white">{Math.min(currentPage * pageSize, records.length)}</span> of <span className="font-semibold text-white">{records.length}</span> records
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className="px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold disabled:opacity-40 disabled:hover:bg-white/5 transition"
                  >
                    ← Previous
                  </button>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className="px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold disabled:opacity-40 disabled:hover:bg-white/5 transition"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isPopup && (
            <div className="mt-5 sm:mt-6 flex justify-start">
              <button onClick={handleBack} className="px-5 sm:px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold text-sm transition">← Back</button>
            </div>
          )}
        </div>
      </div>

      {/* Raise Ticket Modal */}
      <AnimatePresence>
        {ticketModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-md bg-slate-900 rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-white mb-1">🎫 Raise Edit Ticket</h3>
              <p className="text-white/50 text-xs sm:text-sm mb-4">Describe the issue. Admin will review and approve the correction.</p>
              <textarea rows={4} value={ticketReason} onChange={(e) => setTicketReason(e.target.value)}
                placeholder="e.g. My check-in time is recorded incorrectly — I clocked in at 9:00 AM."
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 resize-none focus:outline-none focus:border-cyan-400" />
              {ticketError && <p className="text-red-400 text-xs mt-2">{ticketError}</p>}
              <div className="flex gap-3 mt-4">
                <button onClick={() => { setTicketModal(null); setTicketReason(""); setTicketError(""); }}
                  className="flex-1 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition">Cancel</button>
                <button onClick={handleRaiseTicket} disabled={ticketSubmitting}
                  className="flex-1 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-sm font-bold transition">
                  {ticketSubmitting ? "Submitting…" : "Submit Ticket"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remote Approval Modal */}
      <AnimatePresence>
        {showRemoteModal && (
          <RemoteModal employeeId={targetEmployee} onClose={() => setShowRemoteModal(false)}
            onApproved={() => { loadAttendance(); loadPendingRemoteCount(); }} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showWfhPeriodModal && (
          <WfhPeriodManagementModal employeeId={targetEmployee} onClose={() => setShowWfhPeriodModal(false)}
            onAction={() => { loadPendingWfhCount(); }} />
        )}
      </AnimatePresence>
    </>
  );

  if (isPopup) {
    return (
      <div className="w-full text-white text-xs sm:text-sm overflow-y-auto max-h-[85vh] no-scrollbar relative" style={{ scrollbarWidth: 'none' }}>
        {reportContent}
      </div>
    );
  }

  return (
    <Layout>
      {reportContent}
    </Layout>
  );
}

export default AttendanceReport;
